import { EngagementLocalizationV1DTO, EngagementScheduleV1DTO, EngagementV1DTO } from "@beable/roster-backend-client-v1";
import { UserExperience } from '@beable/content-types';
import { createUUID } from '@beable/binary-uuid-ts';
import React from "react";
import NewsIcon from './icons/NewsIcon';
import ContestIcon from './icons/ContestIcon';
import BrokenImageRoundedIcon from '@mui/icons-material/BrokenImageRounded';
export const EngagementLocalizationLanguages = ['en', 'es'];
export type EngagementVisibilityValues = 'AllSites' | 'AllSitesExcept' | 'SpecificSites';
export interface ScheduleSiteVisibility {
    visibility: EngagementVisibilityValues;
 }
 
export type EngagementScheduleWithSiteVisibility = EngagementScheduleV1DTO & ScheduleSiteVisibility;
 
export interface EditableEngagement extends EngagementV1DTO {
   schedules: EngagementScheduleWithSiteVisibility[]
 }

 /**
  * Finds the first available Localization defaulting to English if no Localizations are found
  */
export function findSetLocalization(engagement: EngagementV1DTO) {
   const localization = EngagementLocalizationLanguages.reduce<EngagementLocalizationV1DTO | undefined>((res, languageCode) => {
    if(!res) {
     const languageLocalization = engagement.localizations.find(localization => localization.language === languageCode);
     if(languageLocalization) {
      res = languageLocalization;
     }
    }
    return res;
   }, undefined);
   return localization ? localization : emptyLocalization({ language: 'en' });
}

/**
 * Transforms a EngagementV1DTO to an EditableEngagement
 */
export const toEditableEngagement = (engagement: EngagementV1DTO): EditableEngagement => {
   const getScheduleVisibility = (schedule: EngagementScheduleV1DTO): EngagementVisibilityValues => {
     if (!schedule.rules.length) {
       return 'AllSites'
     }
     return schedule.rules.every(rule => rule.include) ? 'SpecificSites' : 'AllSitesExcept';
   };
 
   return {
     ...engagement,
     // Ensure we always have Localizations set in the UI for each expected Language
     localizations: EngagementLocalizationLanguages.map(language => {
      const languageLocalization = engagement.localizations.find(localization => localization.language === language);
      return languageLocalization ? languageLocalization : emptyLocalization({ language })
     }),
     schedules: engagement.schedules.map(schedule => ({
       ...schedule,
       visibility: getScheduleVisibility(schedule)
     }))
   };
 };
 
 export const allUserExperiences = Object.values(UserExperience);
 
 export const emptySchedule = (initialValues: Partial<EngagementScheduleWithSiteVisibility> = {}): EngagementScheduleWithSiteVisibility => {
  const startAt = new Date();
  startAt.setHours(0,0,0,0);
  return ({
    id: createUUID(),
    active: true,
    allSites: true,
    startAt,
    endAt: null,
    rules: [],
    visibility: 'AllSites',
    ...initialValues
   })
};

 export const emptyLocalization = (initialValues: Partial<EngagementLocalizationV1DTO> = {}): EngagementLocalizationV1DTO => ({
    id: createUUID(),
    language: 'en',
    title: '',
    subtext: '',
    buttonText: '',
    externalLink: '',
    ...initialValues
 });

 export const emptyEngagement = (initialValues: Partial<EditableEngagement> = {}): EditableEngagement => ({
   id: createUUID(),
   active: false,
   schedules: [
     emptySchedule()
   ],
   localizations: [
     emptyLocalization({ language: 'en' }),
     emptyLocalization({ language: 'es' })
   ],
   activeExperiences: allUserExperiences,
   icon: 'ContestIcon',
   ...initialValues
 });
 
 export const iconMap: { [iconName: string]: { component: React.ReactNode, label: string }} = {
  'NewsIcon': {
    component: <NewsIcon />,
    label: 'News'
  },
  'ContestIcon': {
    component: <ContestIcon />,
    label: 'Contest'
  },
  'NotFound': {
    component: <BrokenImageRoundedIcon />,
    label: 'None'
  }
};

export const getEngagementIcon = (iconName: string) => {
  const icon = (iconName in iconMap) ? iconMap[iconName] : iconMap['NotFound'];
  return icon.component;
};

export interface ToastNotification { 
  error: boolean;
  message: string;
}